import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { visibleModules } from '../lib/modules';
import { MODULE_BRAND_ICON } from '../lib/moduleIcons';

/**
 * The module picker, shown once per sign-in.
 *
 * WHY A PICKER AT ALL: the product is now six distinct things sharing one
 * login. Dropping somebody straight into My Day quietly tells them the app
 * IS My Day, and the other five are discovered by accident. One screen,
 * once a session, makes the whole shape visible.
 *
 * SESSION, NOT FOREVER. It shows on every sign-in, so a new module appears
 * to everybody the day it ships - nothing to remember, nothing to migrate.
 * sessionStorage (not localStorage) is what keeps it to once per sign-in
 * rather than once per page load: it clears when the tab closes, which is
 * close enough to "a session" without storing anything server-side.
 *
 * Not dismissible by clicking away, and no close button: with six modules
 * and no default, an accidental dismissal would leave somebody on a blank
 * shell wondering what to do. Every exit is a choice of module.
 */

const SEEN_KEY = 'cgtodo.modulePicker.seen';

/** Has the picker already run this sign-in? Storage can throw in a private
 *  window, so a failure means "show it" - a duplicate prompt beats a
 *  swallowed one. */
export function pickerAlreadyShown(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

export function markPickerShown(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* private window - it will simply ask again */
  }
}

/**
 * Called by logout. sessionStorage survives a sign-out - it clears when the
 * TAB closes, not when the user does - so without this, signing out and
 * back in inside one tab skipped the picker entirely, which is the one
 * thing it must never do.
 */
export function resetPickerForNextLogin(): void {
  try {
    sessionStorage.removeItem(SEEN_KEY);
  } catch {
    /* nothing stored, nothing to clear */
  }
}

export function ModulePicker({ onPick }: { onPick: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const modules = visibleModules(user?.role.systemKey);

  // The page behind must not scroll while this is up.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  function choose(to: string) {
    markPickerShown();
    onPick();
    navigate(to);
  }

  const firstName = user?.displayName?.split(' ')[0] ?? '';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="module-picker-title"
    >
      <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl sm:p-8">
        <h1 id="module-picker-title" className="text-xl font-semibold text-slate-900">
          {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Where would you like to start? Everything stays one click away.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {modules.map((m) => {
            const Brand = MODULE_BRAND_ICON[m.id];
            return (
            <button
              key={m.id}
              type="button"
              onClick={() => choose(m.home)}
              className="group flex flex-col items-start gap-2 rounded-xl border border-slate-200 p-4 text-left transition hover:border-brand-400 hover:bg-brand-50/60 focus:outline-none focus:ring-2 focus:ring-brand-200"
            >
              {/*
                The brand mark carries its own colours, so the plate behind
                it stays neutral - tinting it per module as well would give
                six differently-coloured cards and no hierarchy. The plate
                reacts to hover; the mark does not.
              */}
              <span className="grid h-11 w-11 place-items-center rounded-lg bg-slate-100 transition group-hover:bg-white">
                <Brand className="h-7 w-7" />
              </span>
              <span className="text-sm font-semibold text-slate-900">{m.label}</span>
              <span className="text-xs leading-snug text-slate-500">{m.blurb}</span>
            </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
